import { useNavigate } from 'react-router-dom';
import { CirclePlay, History } from 'lucide-react';
import { Chargement, MessageErreur } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import { dureeLisible } from '../../lib/useElearning';
import { tempsLisible } from '../../lib/temps-video';
import { adresseLecon, nombreDeRessources, useMesCoursEnLigne } from '../../lib/useLecteur';
import { BarreAvancement, IconeLecon, leconsTerminees, tauxLisible } from './Progression';
import { DevoirsDuModule } from './DevoirsDuModule';

/**
 * Onglet « Cours en ligne » du lecteur (TF-444) : les cours publiés de la
 * formation de l'apprenant, module par module ; une leçon s'ouvre dans le
 * lecteur. Le dossier qui n'est pas encore en formation lit pourquoi.
 *
 * Un cours déjà regardé se reprend à sa dernière leçon, ou à la suivante si
 * elle est vue (TF-449) ; sinon il se commence à sa première leçon qui a de
 * quoi se lire. L'API le dit (`prochaineLecon`, la règle du tableau de bord),
 * la leçon reprend sa vidéo d'elle-même.
 *
 * Chaque cours dit le bloc du programme qu'il couvre, son taux de complétion
 * et celui de chaque module ; une leçon terminée est cochée (TF-451). Chaque
 * module liste ses devoirs et quiz, la fin du cours les siens (TF-454).
 */
export function CoursEnLigne(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, error } = useMesCoursEnLigne(true);

  if (isLoading) return <Chargement libelle="Chargement de vos cours en ligne…" />;
  if (error) {
    return (
      <MessageErreur
        message={error instanceof ErreurApi ? error.message : 'Vos cours en ligne n’ont pas pu être chargés.'}
      />
    );
  }
  if (!data) return <MessageErreur message="Vos cours en ligne n’ont pas pu être chargés." />;
  if (data.refus) return <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">{data.refus}</p>;
  if (data.cours.length === 0) {
    return (
      <p className="text-sm text-codialis-gray">
        Aucun cours en ligne n’est encore publié pour {data.formation ? `« ${data.formation} »` : 'votre formation'}.
      </p>
    );
  }

  return (
    <ul className="space-y-4">
      {data.cours.map((cours) => {
        const debut = cours.prochaineLecon?.id ?? null;
        return (
          <li key={cours.id} className="rounded-2xl border border-gray-100 p-4 sm:p-5">
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1 basis-56">
                {cours.bloc && (
                  <span className="badge-info mb-1.5 inline-block max-w-full truncate rounded px-2 py-0.5 text-[10px] font-medium">
                    Bloc {cours.bloc.numero} · {cours.bloc.libelle}
                  </span>
                )}
                <h4 className="font-display font-bold text-codialis-navy">{cours.titre}</h4>
                <p className="text-xs text-codialis-gray">Par {cours.auteur}</p>
                {cours.description && (
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-codialis-gray">{cours.description}</p>
                )}
                {cours.reprise && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-codialis-navy">
                    <History className="mt-px h-3.5 w-3.5 shrink-0 text-codialis-sky" aria-hidden />
                    <span className="min-w-0">
                      Leçon {cours.reprise.numero} · {cours.reprise.titre}
                      {cours.reprise.positionSecondes !== null && ` · à ${tempsLisible(cours.reprise.positionSecondes)}`}
                    </span>
                  </p>
                )}
              </div>
              {debut && (
                <button
                  type="button"
                  onClick={() => navigate(adresseLecon(debut))}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
                >
                  <CirclePlay className="h-4 w-4" aria-hidden />
                  {cours.reprise ? 'Reprendre' : 'Commencer'}
                </button>
              )}
            </div>
            {cours.avancement.taux !== null && (
              <div className="mt-3">
                <div className="mb-1 flex justify-between gap-2 text-xs">
                  <span className="text-codialis-gray">{leconsTerminees(cours.avancement)}</span>
                  <span className="font-semibold text-codialis-mint-clair">{tauxLisible(cours.avancement)}</span>
                </div>
                <BarreAvancement avancement={cours.avancement} libelle={`Progression du cours ${cours.titre}`} hauteur="h-2" />
              </div>
            )}
            <ol className="mt-4 space-y-3">
              {cours.modules.map((module, rangModule) => (
                <li key={module.id}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="min-w-0 text-xs font-semibold uppercase tracking-wide text-codialis-gray">
                      Module {rangModule + 1} · {module.titre}
                    </span>
                    {module.avancement && module.avancement.taux !== null && (
                      <span
                        className={`shrink-0 text-xs font-bold ${module.avancement.taux === 100 ? 'text-codialis-mint-clair' : 'text-codialis-gray'}`}
                      >
                        {tauxLisible(module.avancement)}
                      </span>
                    )}
                  </div>
                  <ol className="mt-1 divide-y divide-gray-100">
                    {module.lecons.map((lecon, rangLecon) => (
                      <li key={lecon.id}>
                        <button
                          type="button"
                          onClick={() => navigate(adresseLecon(lecon.id))}
                          className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-gray-50"
                        >
                          <IconeLecon lecon={lecon} taille="h-5 w-5" />
                          <span className="w-8 shrink-0 font-mono text-xs text-codialis-gray">
                            {rangModule + 1}.{rangLecon + 1}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-codialis-navy">
                            {lecon.titre}
                          </span>
                          <span className="shrink-0 text-xs text-codialis-gray">
                            {lecon.video
                              ? (dureeLisible(lecon.dureeSecondes) ?? 'Vidéo')
                              : lecon.ressources > 0
                                ? nombreDeRessources(lecon.ressources)
                                : 'Vidéo à venir'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ol>
                  <DevoirsDuModule devoirs={module.devoirs} taille="large" />
                </li>
              ))}
              {cours.evaluations.length > 0 && (
                <li>
                  <span className="text-xs font-semibold uppercase tracking-wide text-codialis-gray">Évaluations du cours</span>
                  <DevoirsDuModule devoirs={cours.evaluations} taille="large" />
                </li>
              )}
            </ol>
          </li>
        );
      })}
    </ul>
  );
}
